import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Eye, Headphones, Users, Download, TrendingUp, Clock, Calendar } from "lucide-react";
import LatestComments from "@/components/LatestComments";

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-coal border border-white/10 p-4 md:p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-widest font-outfit">
      <Icon size={14} className="text-hotpink" /> {label}
    </div>
    <div className="brush-title text-4xl md:text-5xl text-white mt-1">{value?.toLocaleString?.() ?? value}</div>
    {sub && <div className="scribble text-hotpink text-base mt-1">{sub}</div>}
  </div>
);

const fmtDate = (iso) => {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
};

const Bar = ({ value, max, color = "bg-hotpink" }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex-1 bg-ink h-5 relative overflow-hidden">
      <div className={`${color} h-full transition-all`} style={{ width: `${pct}%` }} />
      <span className="absolute inset-0 flex items-center px-2 text-[10px] font-outfit font-bold text-white">
        {value}
      </span>
    </div>
  );
};

export default function StatsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "could not load stats");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="bg-coal border border-white/10 p-5 mb-8 text-white/60 font-outfit">loading stats...</div>;
  if (error) return <div className="bg-coal border border-red-900 p-5 mb-8 text-red-400 font-outfit">{error}</div>;
  if (!stats) return null;

  const { totals, today, daily, per_episode, recent_downloads, by_hour, by_weekday, per_episode_hours } = stats;
  const maxVisits = Math.max(1, ...daily.map(d => d.visits));
  const maxListeners = Math.max(1, ...daily.map(d => d.listeners));
  const maxEpPlays = Math.max(1, ...per_episode.map(p => p.plays));
  const maxHour = Math.max(1, ...(by_hour || []).map(h => h.count));
  const maxWeekday = Math.max(1, ...(by_weekday || []).map(w => w.count));
  const peakHour = (by_hour || []).reduce((best, h) => h.count > (best?.count ?? -1) ? h : best, null);
  const peakWeekday = (by_weekday || []).reduce((best, w) => w.count > (best?.count ?? -1) ? w : best, null);
  const topEps = per_episode;
  const fmtHour = (h) => {
    const period = h < 12 ? "am" : "pm";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return hr + period;
  };

  return (
    <div className="mb-10" data-testid="admin-stats-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="scribble text-hotpink text-2xl rotate-[-2deg] inline-block">live numbers</p>
          <h2 className="brush-title text-3xl md:text-4xl text-white">THE <span className="text-hotpink">DATA.</span></h2>
        </div>
        <button onClick={load} data-testid="stats-refresh" className="text-xs uppercase tracking-widest font-outfit font-bold text-white border border-white/20 hover:border-hotpink hover:text-hotpink px-4 py-2 transition-colors">
          refresh
        </button>
      </div>

      {/* Visits headline — total + today + 7-day average */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="visits-headline">
        <div className="bg-hotpink/10 border-2 border-hotpink/60 p-5">
          <div className="flex items-center gap-2 text-hotpink text-xs uppercase tracking-widest font-outfit font-bold">
            <Eye size={14}/> Total visits
          </div>
          <div className="brush-title text-5xl md:text-6xl text-white mt-1" data-testid="visits-total">{totals.visits?.toLocaleString?.() ?? totals.visits}</div>
          <div className="scribble text-hotpink text-base mt-1">all time</div>
        </div>
        <div className="bg-coal border border-white/20 p-5">
          <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-widest font-outfit">
            <Eye size={14} className="text-hotpink"/> Visits today
          </div>
          <div className="brush-title text-5xl md:text-6xl text-white mt-1" data-testid="visits-today">{today.visits?.toLocaleString?.() ?? today.visits}</div>
          <div className="scribble text-hotpink text-base mt-1">since midnight UTC</div>
        </div>
        <div className="bg-coal border border-white/20 p-5">
          <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-widest font-outfit">
            <TrendingUp size={14} className="text-hotpink"/> 7-day avg
          </div>
          <div className="brush-title text-5xl md:text-6xl text-white mt-1" data-testid="visits-avg7">
            {Math.round(daily.slice(-7).reduce((s, d) => s + (d.visits || 0), 0) / 7).toLocaleString()}
          </div>
          <div className="scribble text-hotpink text-base mt-1">visits per day</div>
        </div>
      </div>

      {/* Totals grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat icon={Eye} label="Visitors" value={totals.visits} sub={`${today.visits} today`} />
        <Stat icon={Headphones} label="Plays" value={totals.plays} sub={`${today.listeners} listeners today`} />
        <Stat icon={Users} label="Listeners" value={totals.unique_listeners} sub={`${totals.users} signed-up`} />
        <Stat icon={Download} label="Downloads" value={totals.downloads} sub={`${totals.paid_unlocks} paid · ${totals.share_unlocks} shared`} />
      </div>

      {/* Daily charts */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-coal border border-white/10 p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-hotpink" />
            <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest">Daily visitors · last 14 days</h3>
          </div>
          <div className="space-y-1.5">
            {daily.map(d => (
              <div key={d.date} className="flex items-center gap-3">
                <span className="w-14 text-[10px] uppercase tracking-widest text-white/50 font-outfit">{fmtDate(d.date)}</span>
                <Bar value={d.visits} max={maxVisits} />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-coal border border-white/10 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Headphones size={16} className="text-hotpink" />
            <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest">Daily listeners · last 14 days</h3>
          </div>
          <div className="space-y-1.5">
            {daily.map(d => (
              <div key={d.date} className="flex items-center gap-3">
                <span className="w-14 text-[10px] uppercase tracking-widest text-white/50 font-outfit">{fmtDate(d.date)}</span>
                <Bar value={d.listeners} max={maxListeners} color="bg-white" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* When listeners tune in */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-coal border border-white/10 p-5" data-testid="stats-by-hour">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-hotpink"/>
              <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest">Hour of day</h3>
            </div>
            {peakHour && peakHour.count > 0 && (
              <span className="scribble text-hotpink text-base">peak · {fmtHour(peakHour.hour)}</span>
            )}
          </div>
          <div className="flex items-end gap-[3px] h-32">
            {by_hour.map(h => {
              const pct = maxHour > 0 ? Math.max(2, Math.round((h.count / maxHour) * 100)) : 0;
              const isPeak = peakHour && h.hour === peakHour.hour && h.count > 0;
              return (
                <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full group" title={fmtHour(h.hour) + " · " + h.count + " plays"}>
                  <div className={"w-full transition-all " + (isPeak ? "bg-hotpink" : "bg-hotpink/40 group-hover:bg-hotpink/70")} style={{ height: pct + "%" }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2 text-[10px] uppercase tracking-widest text-white/40 font-outfit">
            <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
          </div>
          <p className="text-[10px] text-white/30 mt-2 font-outfit">based on listener's local time</p>
        </div>

        <div className="bg-coal border border-white/10 p-5" data-testid="stats-by-weekday">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-hotpink"/>
              <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest">Day of week</h3>
            </div>
            {peakWeekday && peakWeekday.count > 0 && (
              <span className="scribble text-hotpink text-base">peak · {peakWeekday.label}</span>
            )}
          </div>
          <div className="space-y-2">
            {by_weekday.map(w => {
              const pct = maxWeekday > 0 ? Math.max(2, Math.round((w.count / maxWeekday) * 100)) : 0;
              const isPeak = peakWeekday && w.weekday === peakWeekday.weekday && w.count > 0;
              return (
                <div key={w.weekday} className="flex items-center gap-3" data-testid={"weekday-" + w.label.toLowerCase()}>
                  <span className="w-10 text-[10px] uppercase tracking-widest text-white/50 font-outfit">{w.label}</span>
                  <div className="flex-1 bg-ink h-5 relative overflow-hidden">
                    <div className={"h-full transition-all " + (isPeak ? "bg-hotpink" : "bg-hotpink/50")} style={{ width: pct + "%" }} />
                    <span className="absolute inset-0 flex items-center px-2 text-[10px] font-outfit font-bold text-white">{w.count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Per-episode table */}
      <div className="bg-coal border border-white/10 p-5 mb-6">
        <div className="flex items-baseline gap-3 mb-4">
          <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest">Episodes by plays</h3>
          <span className="text-white/40 text-[10px] uppercase tracking-widest font-outfit">{topEps.length} total · scroll for all</span>
        </div>
        {topEps.length === 0 ? (
          <p className="text-white/40 font-outfit text-sm">no plays yet — share the link and watch the numbers climb.</p>
        ) : (
          <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1" data-testid="episodes-plays-list">
            {topEps.map(ep => (
              <div key={ep.episode_number} className="flex items-center gap-3 text-sm" data-testid={`stat-episode-${ep.episode_number}`}>
                <span className="brush-title text-2xl text-white w-10 leading-none">{String(ep.episode_number).padStart(2, "0")}</span>
                <span className="flex-1 font-outfit text-white/80 truncate">{ep.title}</span>
                {ep.is_free && <span className="scribble text-hotpink text-base">free</span>}
                <Bar value={ep.plays} max={maxEpPlays} />
                <span className="w-20 text-right text-xs text-white/50 font-outfit">{ep.unique_listeners} 👤</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-episode hour signature */}
      {per_episode_hours && per_episode_hours.length > 0 && (
        <div className="bg-coal border border-white/10 p-5 mb-6" data-testid="stats-episode-hours">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="text-hotpink"/>
            <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest">When each episode gets played</h3>
          </div>
          <p className="scribble text-hotpink text-base mb-4">spot the late-night listens vs. the commute ones</p>
          <div className="space-y-3">
            {per_episode_hours.slice(0, 10).map((row) => {
              const maxBar = Math.max(1, ...row.hours);
              return (
                <div key={row.episode_number} className="flex items-center gap-3" data-testid={"ep-hours-" + row.episode_number}>
                  <span className="brush-title text-xl text-white w-8 leading-none flex-shrink-0">{String(row.episode_number).padStart(2, "0")}</span>
                  <span className="font-outfit text-white/80 text-xs md:text-sm w-32 md:w-40 truncate flex-shrink-0">{row.title}</span>
                  <div className="flex-1 flex items-end gap-[2px] h-10 min-w-0">
                    {row.hours.map((c, h) => {
                      const pct = maxBar > 0 ? Math.max(c > 0 ? 4 : 0, Math.round((c / maxBar) * 100)) : 0;
                      const isPeak = h === row.peak_hour && c > 0;
                      return (
                        <div key={h} className="flex-1 flex flex-col justify-end h-full" title={fmtHour(h) + ": " + c + " plays"}>
                          <div className={"w-full transition-all " + (isPeak ? "bg-hotpink" : c > 0 ? "bg-hotpink/45" : "bg-white/5")} style={{ height: pct + "%" }} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-right flex-shrink-0 w-24 md:w-28">
                    <div className="scribble text-hotpink text-base leading-none">{row.peak_hour !== null ? "peak " + fmtHour(row.peak_hour) : "—"}</div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest font-outfit mt-0.5">{row.total} plays</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent downloads */}
      {recent_downloads?.length > 0 && (
        <div className="bg-coal border border-white/10 p-5 mb-6">
          <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest mb-4">Recent downloads · last 30</h3>
          <div className="space-y-1 text-xs font-outfit max-h-72 overflow-y-auto">
            {recent_downloads.map(d => (
              <div key={d.id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-1">
                <span className="text-white/80 truncate flex-1">{d.user_email}</span>
                <span className="text-hotpink font-bold">Ep {d.episode_number}</span>
                <span className="text-white/40 w-32 text-right">{new Date(d.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Latest comments */}
      <div className="bg-coal border border-white/10 p-5">
        <LatestComments limit={6} compact />
      </div>
    </div>
  );
}
